import { useState, useRef, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { ConfigureModal } from "./ConfigureModal";
import { ConnectModal } from "./ConnectModal";
import {
  leadSourceContainers,
  type LeadSourceContainer,
  type SourceAccount,
  type SourceAccountStatus,
  type SourceContainerStatus,
} from "@/mocks/integrationsData";

const accountStatusBadge: Record<SourceAccountStatus, { label: string; classes: string }> = {
  connected: { label: "Connected", classes: "bg-green-50 text-green-700 border border-green-200" },
  error: { label: "Error", classes: "bg-red-50 text-red-600 border border-red-200" },
  inactive: { label: "Inactive", classes: "bg-gray-100 text-gray-500 border border-gray-200" },
};

const sourceStatusConfig: Record<SourceContainerStatus, { dot: string; label: string }> = {
  all_active: { dot: "bg-green-500", label: "All Active" },
  has_error: { dot: "bg-red-500", label: "1 Error" },
  not_connected: { dot: "bg-gray-400", label: "Not Connected" },
};

export function LeadSourceContainers() {
  const { showToast } = useToast();
  const [containers, setContainers] = useState<LeadSourceContainer[]>(leadSourceContainers);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set(["indiamart"]));
  const [configuringAccount, setConfiguringAccount] = useState<SourceAccount | null>(null);
  const [configuringSourceName, setConfiguringSourceName] = useState<string>("");
  const [connectingSource, setConnectingSource] = useState<string | null>(null);
  const [menuAccountId, setMenuAccountId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<{ sourceId: string; accountId: string; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [disconnectTarget, setDisconnectTarget] = useState<{ sourceId: string; accountId: string } | null>(null);
  const [addAccountSource, setAddAccountSource] = useState<string | null>(null);
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountIdentifier, setNewAccountIdentifier] = useState("");

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuAccountId(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const toggleExpand = (sourceId: string) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  };

  const containerStatusSummary = (container: LeadSourceContainer) => {
    if (container.accounts.length === 0) return sourceStatusConfig.not_connected;
    const hasError = container.accounts.some((a) => a.status === "error");
    if (hasError) return sourceStatusConfig.has_error;
    const allConnected = container.accounts.every((a) => a.status === "connected");
    return allConnected ? sourceStatusConfig.all_active : sourceStatusConfig.not_connected;
  };

  const addAccount = () => {
    if (!addAccountSource || !newAccountName.trim() || !newAccountIdentifier.trim()) {
      showToast("Please fill in all fields", "error");
      return;
    }
    setContainers((prev) =>
      prev.map((c) => {
        if (c.id !== addAccountSource) return c;
        const newAccount: SourceAccount = {
          id: `${addAccountSource}-${Date.now()}`,
          name: newAccountName.trim(),
          identifier: newAccountIdentifier.trim(),
          status: "connected",
          leadsToday: 0,
          lastSync: "Just now",
          method: c.id === "whatsapp" ? "Meta Cloud API" : c.id === "indiamart" ? "REST API + Webhook" : "Webhook Push",
          syncFrequency: "Real-time",
          autoAssign: false,
        };
        const updatedAccounts = [...c.accounts, newAccount];
        const totalLeads = updatedAccounts.reduce((sum, a) => sum + a.leadsToday, 0);
        const allActive = updatedAccounts.every((a) => a.status === "connected");
        return {
          ...c,
          accounts: updatedAccounts,
          totalLeadsToday: totalLeads,
          statusSummary: allActive ? "all_active" : c.statusSummary,
        };
      })
    );
    showToast(`Account "${newAccountName}" added to ${containers.find((c) => c.id === addAccountSource)?.name}`, "success");
    setAddAccountSource(null);
    setNewAccountName("");
    setNewAccountIdentifier("");
  };

  const handleDisconnect = () => {
    if (!disconnectTarget) return;
    setContainers((prev) =>
      prev.map((c) => {
        if (c.id !== disconnectTarget.sourceId) return c;
        const updatedAccounts = c.accounts.filter((a) => a.id !== disconnectTarget.accountId);
        const totalLeads = updatedAccounts.reduce((sum, a) => sum + a.leadsToday, 0);
        const hasError = updatedAccounts.some((a) => a.status === "error");
        const allDisconnected = updatedAccounts.length === 0;
        return {
          ...c,
          accounts: updatedAccounts,
          totalLeadsToday: totalLeads,
          statusSummary: allDisconnected ? "not_connected" : hasError ? "has_error" : "all_active",
        };
      })
    );
    const account = containers.find((c) => c.id === disconnectTarget.sourceId)?.accounts.find((a) => a.id === disconnectTarget.accountId);
    showToast(`${account?.name ?? "Account"} disconnected`, "info");
    setDisconnectTarget(null);
  };

  const handlePause = (sourceId: string, accountId: string) => {
    setContainers((prev) =>
      prev.map((c) => {
        if (c.id !== sourceId) return c;
        const updatedAccounts = c.accounts.map((a) =>
          a.id === accountId
            ? { ...a, status: "inactive" as SourceAccountStatus }
            : a
        );
        return { ...c, accounts: updatedAccounts };
      })
    );
    const account = containers.find((c) => c.id === sourceId)?.accounts.find((a) => a.id === accountId);
    showToast(`${account?.name ?? "Account"} paused — will stop receiving leads`, "info");
    setMenuAccountId(null);
  };

  const handleResume = (sourceId: string, accountId: string) => {
    setContainers((prev) =>
      prev.map((c) => {
        if (c.id !== sourceId) return c;
        const updatedAccounts = c.accounts.map((a) =>
          a.id === accountId
            ? { ...a, status: "connected" as SourceAccountStatus, lastSync: "Just now" }
            : a
        );
        const allActive = updatedAccounts.every((a) => a.status === "connected");
        return { ...c, accounts: updatedAccounts, statusSummary: allActive ? "all_active" : c.statusSummary };
      })
    );
    const account = containers.find((c) => c.id === sourceId)?.accounts.find((a) => a.id === accountId);
    showToast(`${account?.name ?? "Account"} resumed`, "success");
    setMenuAccountId(null);
  };

  const handleRename = () => {
    if (!renameTarget || !renameValue.trim()) return;
    setContainers((prev) =>
      prev.map((c) => {
        if (c.id !== renameTarget.sourceId) return c;
        const updatedAccounts = c.accounts.map((a) =>
          a.id === renameTarget.accountId ? { ...a, name: renameValue.trim() } : a
        );
        return { ...c, accounts: updatedAccounts };
      })
    );
    showToast("Account renamed", "success");
    setRenameTarget(null);
    setRenameValue("");
    setMenuAccountId(null);
  };

  const handleViewLogs = (sourceId: string, accountId: string) => {
    const account = containers.find((c) => c.id === sourceId)?.accounts.find((a) => a.id === accountId);
    showToast(`Viewing logs for ${account?.name ?? "account"}`, "info");
    const el = document.getElementById("integration-activity-log");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuAccountId(null);
  };

  const handleConnectSource = (sourceId: string) => {
    if (sourceId === "whatsapp") {
      setConnectingSource(sourceId);
      return;
    }
    setContainers((prev) =>
      prev.map((c) => {
        if (c.id !== sourceId) return c;
        const newAccount: SourceAccount = {
          id: `${sourceId}-${Date.now()}`,
          name: `${c.name} — Main`,
          identifier: sourceId === "indiamart" ? `API ID: IND-${new Date().getFullYear()}-MAIN` : `Webhook ID: ${c.name.toUpperCase()}-WH-MAIN`,
          status: "connected",
          leadsToday: 0,
          lastSync: "Just now",
          method: sourceId === "indiamart" ? "REST API + Webhook" : "Webhook Push",
          syncFrequency: "Every 5 mins",
          autoAssign: true,
        };
        return { ...c, accounts: [newAccount], totalLeadsToday: 0, statusSummary: "all_active" };
      })
    );
    const name = containers.find((c) => c.id === sourceId)?.name;
    showToast(`${name} connected successfully`, "success");
    setExpandedSources((prev) => new Set([...prev, sourceId]));
  };

  const handleWhatsAppConnected = () => {
    setContainers((prev) =>
      prev.map((c) => {
        if (c.id !== "whatsapp") return c;
        return {
          ...c,
          accounts: [
            {
              id: "whatsapp-sales",
              name: "+91-98765-43210 Sales Team",
              identifier: "Meta Cloud API",
              status: "connected",
              leadsToday: 0,
              lastSync: "Just now",
              method: "Meta Cloud API",
              syncFrequency: "Real-time",
              autoAssign: true,
            },
          ],
          totalLeadsToday: 0,
          statusSummary: "all_active",
        };
      })
    );
    setConnectingSource(null);
    setExpandedSources((prev) => new Set([...prev, "whatsapp"]));
  };

  const getChevronIcon = (sourceId: string) => {
    const isExpanded = expandedSources.has(sourceId);
    return isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-[#111827]">Lead Source Integrations</h3>
        <p className="text-sm text-[#6b7280] mt-0.5">
          Connect platforms to auto-import leads
        </p>
      </div>

      <div className="space-y-3">
        {containers.map((container) => {
          const statusSum = containerStatusSummary(container);
          const isExpanded = expandedSources.has(container.id);

          return (
            <div
              key={container.id}
              className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden"
            >
              {/* Container Header */}
              <button
                onClick={() => toggleExpand(container.id)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors cursor-pointer"
              >
                {/* Left */}
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg ${container.logoBg} flex items-center justify-center flex-shrink-0`}
                  >
                    <span className="text-sm font-bold text-white">{container.logoLetters}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#111827]">{container.name}</span>
                    {container.accounts.length > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 whitespace-nowrap">
                        {container.accounts.length} account{container.accounts.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    {container.accounts.length === 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 whitespace-nowrap">
                        No accounts
                      </span>
                    )}
                  </div>
                </div>

                {/* Right */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#6b7280] whitespace-nowrap">
                    {container.totalLeadsToday} lead{container.totalLeadsToday !== 1 ? "s" : ""} today
                  </span>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusSum.dot}`} />
                    <span className="text-xs text-[#6b7280] whitespace-nowrap">{statusSum.label}</span>
                  </div>
                  <div className={`w-5 h-5 flex items-center justify-center transition-transform ${isExpanded ? "rotate-180" : ""}`}>
                    <i className="ri-arrow-down-s-line text-[#9ca3af]" />
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="bg-gray-50 border-t border-[#e5e7eb] px-5 py-4">
                  {container.accounts.length > 0 ? (
                    <div className="space-y-2">
                      {container.accounts.map((account) => (
                        <div
                          key={account.id}
                          className="bg-white rounded-xl border border-[#e5e7eb] p-4 flex items-center justify-between gap-4"
                        >
                          {/* Left: Account info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-[#111827]">{account.name}</span>
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${accountStatusBadge[account.status].classes}`}
                              >
                                {accountStatusBadge[account.status].label}
                              </span>
                            </div>
                            <p className="text-xs text-[#6b7280] mt-0.5">{account.identifier}</p>
                          </div>

                          {/* Middle: Stats */}
                          <div className="flex items-center gap-3 text-xs flex-shrink-0">
                            <div className="text-center">
                              <span className="block font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                                {account.leadsToday}
                              </span>
                              <span className="text-[10px] text-[#9ca3af] mt-0.5">leads today</span>
                            </div>
                            <span className="text-[#9ca3af] whitespace-nowrap">{account.lastSync}</span>
                          </div>

                          {/* Right: Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {account.status === "connected" && (
                              <button
                                onClick={() => {
                                  setConfiguringAccount(account);
                                  setConfiguringSourceName(container.name);
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-gray-100 transition-colors cursor-pointer"
                                title="Configure"
                              >
                                <i className="ri-settings-3-line" />
                              </button>
                            )}
                            {account.status === "error" && (
                              <button
                                onClick={() => {
                                  setContainers((prev) =>
                                    prev.map((c) => {
                                      if (c.id !== container.id) return c;
                                      const updatedAccounts = c.accounts.map((a) =>
                                        a.id === account.id
                                          ? { ...a, status: "connected" as SourceAccountStatus, lastSync: "Just now" }
                                          : a
                                      );
                                      const allActive = updatedAccounts.every((a) => a.status === "connected");
                                      return { ...c, accounts: updatedAccounts, statusSummary: allActive ? "all_active" : c.statusSummary };
                                    })
                                  );
                                  showToast(`${account.name} reconnected`, "success");
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                                title="Reconnect"
                              >
                                <i className="ri-restart-line" />
                              </button>
                            )}
                            <button
                              onClick={() => setDisconnectTarget({ sourceId: container.id, accountId: account.id })}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                              title="Disconnect"
                            >
                              <i className="ri-link-unlink-m" />
                            </button>

                            {/* 3-dot menu */}
                            <div className="relative" ref={menuAccountId === account.id ? menuRef : undefined}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuAccountId(menuAccountId === account.id ? null : account.id);
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                              >
                                <i className="ri-more-2-fill" />
                              </button>

                              {menuAccountId === account.id && (
                                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg border border-[#e5e7eb] shadow-lg py-1 z-30">
                                  <button
                                    onClick={() => {
                                      setRenameTarget({
                                        sourceId: container.id,
                                        accountId: account.id,
                                        name: account.name,
                                      });
                                      setRenameValue(account.name);
                                      setMenuAccountId(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap"
                                  >
                                    <i className="ri-edit-line mr-2 text-xs" />
                                    Rename Account
                                  </button>
                                  {account.status === "connected" && (
                                    <button
                                      onClick={() => handlePause(container.id, account.id)}
                                      className="w-full text-left px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                      <i className="ri-pause-circle-line mr-2 text-xs" />
                                      Pause
                                    </button>
                                  )}
                                  {account.status === "inactive" && (
                                    <button
                                      onClick={() => handleResume(container.id, account.id)}
                                      className="w-full text-left px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                      <i className="ri-play-circle-line mr-2 text-xs" />
                                      Resume
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleViewLogs(container.id, account.id)}
                                    className="w-full text-left px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap"
                                  >
                                    <i className="ri-file-list-3-line mr-2 text-xs" />
                                    View Logs
                                  </button>
                                  <button
                                    onClick={() => {
                                      setDisconnectTarget({ sourceId: container.id, accountId: account.id });
                                      setMenuAccountId(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors whitespace-nowrap"
                                  >
                                    <i className="ri-delete-bin-line mr-2 text-xs" />
                                    Delete Account
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Add Another Account */}
                      <button
                        onClick={() => {
                          setAddAccountSource(container.id);
                          setNewAccountName("");
                          setNewAccountIdentifier("");
                        }}
                        className="w-full border-2 border-dashed border-indigo-200 rounded-xl p-3 flex items-center justify-center gap-2 text-indigo-600 text-sm hover:bg-indigo-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line" />
                        Add Another {container.name} Account
                      </button>
                    </div>
                  ) : (
                    /* No accounts — connect prompt */
                    <div className="text-center py-6">
                      <div className={`w-12 h-12 rounded-xl ${container.iconBg} flex items-center justify-center mx-auto mb-3`}>
                        <i className={`${container.id === "indiamart" ? "ri-building-4-line" : container.id === "justdial" ? "ri-phone-line" : container.id === "tradeindia" ? "ri-store-3-line" : container.id === "whatsapp" ? "ri-whatsapp-line" : container.id === "website-forms" ? "ri-global-line" : "ri-share-line"} ${container.iconText} text-lg`} />
                      </div>
                      <p className="text-sm text-[#6b7280] mb-3">No accounts connected yet</p>
                      <button
                        onClick={() => handleConnectSource(container.id)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-add-line" />
                        Connect {container.name}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Configure Modal */}
      {configuringAccount && (
        <ConfigureModal
          integration={{
            id: configuringAccount.id,
            name: configuringAccount.name,
            type: configuringAccount.method,
            status: configuringAccount.status as "connected" | "error" | "not_connected",
            logoBg: "",
            logoLetters: "",
            leadsToday: configuringAccount.leadsToday,
            lastSync: configuringAccount.lastSync,
            method: configuringAccount.method,
            apiKey: configuringAccount.apiKey,
            apiSecret: configuringAccount.apiSecret,
            webhookUrl: configuringAccount.webhookUrl,
            webhookSecret: configuringAccount.webhookSecret,
            syncFrequency: configuringAccount.syncFrequency,
            autoAssign: configuringAccount.autoAssign,
            fieldMapping: configuringAccount.fieldMapping,
          }}
          onClose={() => {
            setConfiguringAccount(null);
            setConfiguringSourceName("");
          }}
        />
      )}

      {/* Connect Modal (WhatsApp) */}
      {connectingSource === "whatsapp" && (
        <ConnectModal
          onClose={() => setConnectingSource(null)}
          onConnected={handleWhatsAppConnected}
        />
      )}

      {/* Disconnect Confirmation Modal */}
      {disconnectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setDisconnectTarget(null)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h4 className="text-base font-semibold text-[#111827] mb-2">Disconnect Account?</h4>
            <p className="text-sm text-[#6b7280] mb-5">
              This will stop lead import from this account. Existing lead data will be preserved.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setDisconnectTarget(null)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium text-[#374151] rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnect}
                className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Account Modal */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              setRenameTarget(null);
              setRenameValue("");
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h4 className="text-base font-semibold text-[#111827] mb-3">Rename Account</h4>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-4"
              placeholder="Account name"
              autoFocus
            />
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setRenameTarget(null);
                  setRenameValue("");
                }}
                className="px-4 py-2 border border-gray-300 text-sm font-medium text-[#374151] rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Account Modal */}
      {addAccountSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              setAddAccountSource(null);
              setNewAccountName("");
              setNewAccountIdentifier("");
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h4 className="text-base font-semibold text-[#111827] mb-1">
              Add {containers.find((c) => c.id === addAccountSource)?.name} Account
            </h4>
            <p className="text-xs text-[#6b7280] mb-4">
              Connect an additional account for this source
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Account Name</label>
                <input
                  type="text"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={`e.g. ${containers.find((c) => c.id === addAccountSource)?.name} — Delhi Office`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Account Identifier</label>
                <input
                  type="text"
                  value={newAccountIdentifier}
                  onChange={(e) => setNewAccountIdentifier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="API ID / Webhook ID"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setAddAccountSource(null);
                  setNewAccountName("");
                  setNewAccountIdentifier("");
                }}
                className="px-4 py-2 border border-gray-300 text-sm font-medium text-[#374151] rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={addAccount}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Add Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}